import { resolveTableState } from '../../lib/fohState';
import { useEffect, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import { toMinutes } from '../../lib/bookingQueue';

// The table × time grid — the capacity view ("can I fit a 6 at 8?"). Cells draw
// **bookings** and nothing else: a block spans the booking's duration, and an
// "Unassigned" row at the top holds the ones with no table yet, to drag onto a row. The
// table's live status is a dot on its row header only — it used to tint the whole row,
// which said "occupied" from 11:00 to 23:00 about a table that was busy at 21:00.

const SLOT_MIN = 30;
const SLOT_PX = 80;
const ROW_PX = 64;
const HEAD_PX = 41;
const COL_PX = 112;
const OPEN_MIN = 12 * 60;
const CLOSE_MIN = 23 * 60;

const SLOTS = [];
for (let m = OPEN_MIN; m < CLOSE_MIN; m += SLOT_MIN) {
  SLOTS.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
}

// The table's live state, in the floor's own colours (lib/fohState): a free table is no
// colour, a seated one is grey, reserved is the navy — a green-free / red-occupied dot said
// "good / bad" about a room that is neither.
const liveDot = (t) => resolveTableState(t).rail;

const leftOf = (hhmm) => ((toMinutes(hhmm) - OPEN_MIN) / SLOT_MIN) * SLOT_PX;
const widthOf = (r) => Math.max(SLOT_PX / 2, ((r.duration_minutes || 90) / SLOT_MIN) * SLOT_PX - 8);

// Bookings that overlap in time stack into lanes rather than on top of each other — the
// Unassigned row on a busy night, or a double-booked table, stays readable.
const LANE_PX = 30;
export const packLanes = (blocks) => {
  const sorted = [...blocks].sort((a, b) => toMinutes(a.time) - toMinutes(b.time));
  const laneEnds = [];
  return sorted.map((r) => {
    const start = toMinutes(r.time);
    const end = start + (r.duration_minutes || 90);
    let lane = laneEnds.findIndex((e) => e <= start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(end); } else { laneEnds[lane] = end; }
    return { r, lane };
  });
};
const rowHeight = (lanes) => Math.max(ROW_PX, lanes * LANE_PX + 4);

const Block = ({ r, lane, onDragStart, onDragEnd, onClick, faded }) => (
  <div
    className={`absolute rounded px-1.5 py-0.5 text-[10px] overflow-hidden cursor-grab active:cursor-grabbing z-10 shadow-sm ${
      faded ? 'bg-ink-faint text-ink-soft' : r.status === 'seated' ? 'bg-signal-ok text-white' : 'bg-verlo text-white hover:bg-verlo-hover'
    }`}
    style={{ left: `${leftOf(r.time) + 4}px`, width: `${widthOf(r)}px`, top: `${2 + lane * LANE_PX}px`, height: `${LANE_PX - 4}px` }}
    draggable
    onDragStart={(e) => onDragStart(e, r)}
    onDragEnd={onDragEnd}
    onClick={(e) => { e.stopPropagation(); onClick(r); }}
    title={`${r.customer_name} (${r.party_size}) · ${r.time}`}
    data-testid={`grid-block-${r.id}`}
  >
    <div className="flex items-center gap-1 leading-tight">
      <span className="font-medium truncate">{r.customer_name}</span>
      <span className="opacity-80 shrink-0">· {r.party_size} · {r.time}</span>
    </div>
  </div>
);

const BookingGrid = ({ tables, reservations, isToday, currentTime, onSlotClick, onReservationClick, onMove, slotGone }) => {
  const [dragData, setDragData] = useState(null);
  const scrollRef = useRef(null);
  const nowRef = useRef(null);
  const live = reservations.filter((r) => !['cancelled', 'no_show'].includes(r.status));
  const unassigned = live.filter((r) => !r.table_id);
  const byTable = (id) => live.filter((r) => r.table_id === id);
  const nowMins = isToday ? toMinutes(currentTime) : null;
  const nowLeft = nowMins != null && nowMins >= OPEN_MIN && nowMins <= CLOSE_MIN ? ((nowMins - OPEN_MIN) / SLOT_MIN) * SLOT_PX : null;

  useEffect(() => {
    if (nowRef.current) {
      const t = setTimeout(() => nowRef.current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' }), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isToday]);

  const dragStart = (e, r) => { setDragData(r); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', r.id); };
  const dragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const drop = (e, slot, tableId) => {
    e.preventDefault();
    if (!dragData) return;
    onMove(dragData, slot, tableId);
    setDragData(null);
  };

  const rows = [{ id: null, name: 'Unassigned', unassigned: true }, ...tables].map((t) => {
    const packed = packLanes(t.unassigned ? unassigned : byTable(t.id));
    const lanes = packed.reduce((n, b) => Math.max(n, b.lane + 1), 1);
    return { ...t, packed, height: rowHeight(lanes) };
  });

  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid="booking-grid">
      <div className="flex max-h-[520px]">
        <div className="shrink-0 border-r bg-white z-20" style={{ width: COL_PX }}>
          <div className="p-2 font-medium text-sm text-ink-soft border-b bg-surface-2 flex items-center" style={{ height: HEAD_PX }}>Table</div>
          <div className="overflow-y-hidden" id="booking-grid-rows" style={{ maxHeight: `calc(520px - ${HEAD_PX}px)` }}>
            {rows.map((t) => (
              <div key={t.id || 'unassigned'} className={`p-2 border-b flex flex-col justify-center ${t.unassigned ? 'bg-signal-warn-tint' : 'bg-white'}`} style={{ height: t.height }}>
                {t.unassigned ? (
                  <>
                    <div className="font-medium text-sm text-signal-warn">Unassigned</div>
                    <div className="text-xs text-signal-warn">{unassigned.length} need a table</div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className={`h-2 w-2 rounded-full ${liveDot(t)} ${t.status === 'available' ? 'ring-1 ring-inset ring-ink-faint' : ''}`} title={resolveTableState(t).word} />
                      <span className="font-medium text-sm">{t.name}</span>
                    </div>
                    <div className="text-xs text-ink-soft flex items-center gap-1"><Users size={10} />{t.capacity} seats</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div
          className="flex-1 overflow-x-auto overflow-y-auto"
          ref={scrollRef}
          onScroll={(e) => { const col = document.getElementById('booking-grid-rows'); if (col) col.scrollTop = e.target.scrollTop; }}
        >
          <div className="min-w-max relative">
            {nowLeft != null && (
              <div ref={nowRef} className="absolute top-0 bottom-0 w-0.5 bg-signal-stop-line z-20 pointer-events-none" style={{ left: `${nowLeft}px` }}>
                <div className="absolute top-2 left-1.5 bg-signal-stop-line text-white text-[10px] px-1 rounded whitespace-nowrap">{currentTime}</div>
              </div>
            )}
            <div className="flex border-b bg-surface-2 sticky top-0 z-10">
              {SLOTS.map((s) => (
                <div key={s} className="shrink-0 text-center text-xs font-medium text-ink-soft border-r flex items-center justify-center" style={{ width: SLOT_PX, height: HEAD_PX }}>{s}</div>
              ))}
            </div>
            {rows.map((t) => {
              return (
                <div key={t.id || 'unassigned'} className={`flex border-b relative ${t.unassigned ? 'bg-signal-warn-tint/40' : ''}`} style={{ height: t.height }}>
                  {SLOTS.map((s) => (slotGone?.(s) ? (
                    // A time that has gone: greyed, and neither a new booking nor a drop.
                    <div key={s} className="shrink-0 border-r bg-surface-2 bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(27,26,46,0.04)_6px_8px)] cursor-not-allowed"
                      style={{ width: SLOT_PX }} data-past="1" title="This time has gone" />
                  ) : (
                    <div
                      key={s}
                      className={`shrink-0 border-r cursor-pointer ${dragData ? 'hover:bg-signal-ok-tint hover:ring-2 hover:ring-signal-ok-line hover:ring-inset' : 'hover:bg-signal-ok-tint'}`}
                      style={{ width: SLOT_PX }}
                      onClick={() => !dragData && onSlotClick(s, t.unassigned ? null : t.id)}
                      onDragOver={dragOver}
                      onDrop={(e) => drop(e, s, t.unassigned ? null : t.id)}
                      title={t.unassigned ? 'Book without a table' : 'Book this table'}
                    />
                  )))}
                  {t.packed.map(({ r, lane }) => (
                    <Block key={r.id} r={r} lane={lane} faded={r.status === 'completed'} onDragStart={dragStart} onDragEnd={() => setDragData(null)} onClick={onReservationClick} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingGrid;
